import type { PartnerEnum } from '../groveSchedulerClient'
import { getPartnerBaseUrl } from './partnerEnvironment'
import type { PartnerSchemaInternal } from './types'

/**
 * Build OAuth authorization URL for Dentally
 * Uses the staging base on staging, the UK base in production.
 *
 * @param agentId - The agent ID to authorize
 * @returns Complete OAuth authorization URL
 */
export const buildDentallyOAuthUrl = (agentId: string): string => {
  const base = getPartnerBaseUrl()
  // On staging use the staging base, otherwise always UK
  const dentallyBase =
    base === 'https://partner.staging.mangovoice.net'
      ? base
      : 'https://partner.uk.mangovoice.net'
  return `${dentallyBase}/dentally/authorize?agent_id=${encodeURIComponent(agentId)}`
}

/**
 * Dentally partner configuration schema
 * Requires client_id and client_secret. Customer Admin will complete OAuth in web-app.
 */
export const dentallySchema: PartnerSchemaInternal = {
  partner: 'dentally' as PartnerEnum,
  displayName: 'Dentally',
  fields: [
    {
      key: 'client_id',
      label: 'Client ID',
      type: 'text',
      required: true,
      helperText: 'In Dentally, this field is called `applicant_id`.',
    },
    {
      key: 'client_secret',
      label: 'Client Secret',
      type: 'text',
      required: true,
      helperText: 'In Dentally, this field is labeled `Secret`.',
    },
  ],
  requiresOAuth: true,
  buildOAuthUrl: buildDentallyOAuthUrl,
  postCreationInfo:
    'After creating this agent, the customer will need to authorize with their Dentally credentials. They can do this from their web portal, or you can share the authorization link with them from the agent details page.',
  authHelp:
    'Authorization requires Dentally account credentials with appropriate permissions. If authorization fails, verify the account has the necessary permissions in Dentally.',
}
